import { useIntl } from 'react-intl';
import {
  Form,
  Row,
  Col,
  FormInstance,
  Button,
  Divider,
  Checkbox,
  Tooltip,
} from 'antd';

import { AiOutlineReload } from 'react-icons/ai';
import FloatingFormItem from '@/components/_common/FloatingFormItem';

import {
  InputDate,
  CompaniesInfiniteSelectField,
  BankAccountInfiniteSelectField,
  Group,
} from '@/components/_common';
import { useDebounce, useDefaultEmptyFieldError } from '@/hooks';

import CashMovementList from '../CashMovementList';
import {
  CashMovementFormValues,
  ListCashMovementQuery,
} from '@/core/domain/financial/BoxMovement/BoxMovement.types';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { DateTimeFormat } from '@/utils/moment';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { useCashMovementList } from '@/core/domain/financial/BoxMovement/BoxMovement.hooks';
import { CheckCircle, Info } from 'react-feather';
import './style.less';
import SpaceForButton from '@/components/_common/SpaceForButtons';

interface Props {
  form: FormInstance<CashMovementFormValues>;
}

function CashMovementForm({ form }: Props) {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const [queryParams, setQueryParams] = useState<ListCashMovementQuery>({
    order: 'ASC',
    ...convertParams<ListCashMovementQuery>({
      startDate: 'string',
      finalDate: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const cashMovementList = useCashMovementList({ ...queryParams });

  const handleQueryList = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();

      if (values) {
        const params = {
          ...values,
          startDate: values.startDate?.format(DateTimeFormat),
          finalDate: values.finalDate?.format(DateTimeFormat),
        };

        cashMovementList.remove();
        setQueryParams((prev) => ({ ...prev, ...params }));

        setTimeout(() => {
          setLoading(false);
        }, 4000);
      }
    } catch {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (queryParams) {
      form.setFieldsValue({
        ...queryParams,
        startDate: queryParams.startDate
          ? moment(queryParams.startDate)
          : undefined,
        finalDate: queryParams.finalDate
          ? moment(queryParams.finalDate)
          : undefined,
      });
    }
  }, []);

  const isUpToDate = !cashMovementList.isLoading && loading;

  return (
    <Form layout="vertical" form={form}>
      <Row gutter={[12, 6]} align="top">
        <Col span={24} md={12} lg={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'cashMovement.form.company',
            })}
            name="companyIds"
            rules={[defaultEmptyFieldError]}
          >
            <CompaniesInfiniteSelectField
              mode="multiple"
              allowClear
              activityFilter
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={8}>
          <FloatingFormItem
            name="bankAccountIds"
            label={intl.formatMessage({
              id: 'cashMovement.form.bankAccount',
            })}
            rules={[defaultEmptyFieldError]}
          >
            <BankAccountInfiniteSelectField
              mode="multiple"
              allowClear
              buttonSelectAll
              showActivityFilter
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={4}>
          <FloatingFormItem
            name="startDate"
            label={intl.formatMessage({
              id: 'cashMovement.form.timeCourse',
            })}
            initialValue={moment().startOf('month')}
            rules={[defaultEmptyFieldError]}
          >
            <InputDate />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={4}>
          <FloatingFormItem
            name="finalDate"
            label={intl.formatMessage({
              id: 'cashMovement.form.until',
            })}
            initialValue={moment()}
            rules={[defaultEmptyFieldError]}
          >
            <InputDate />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <SpaceForButton justifyContent="space-between">
            <Group>
              <Form.Item
                noStyle
                initialValue={false}
                name="doNotViewRefund"
                valuePropName="checked"
              >
                <Checkbox>Não contabilizar estorno</Checkbox>
              </Form.Item>

              <Tooltip title="Esta opção oculta apenas os valores dos estornos. Os lançamentos continuarão visíveis na listagem">
                <Info size={20} cursor="pointer" />
              </Tooltip>
            </Group>

            <Button
              type="primary"
              size="large"
              icon={
                isUpToDate ? <CheckCircle size={14} /> : <AiOutlineReload />
              }
              onClick={handleQueryList}
              disabled={isUpToDate}
              loading={cashMovementList.isLoading}
              block
              className="button-update"
              style={{
                background: isUpToDate ? 'green' : undefined,
                color: isUpToDate ? 'white' : undefined,
              }}
            >
              {isUpToDate
                ? 'Atualizado'
                : intl.formatMessage({ id: 'cashMovement.button.updateData' })}
            </Button>
          </SpaceForButton>
        </Col>

        {!!queryParams.companyIds?.length && (
          <>
            <Divider />

            <Col span={24} style={{ marginBottom: 20 }}>
              <CashMovementList cashMovementList={cashMovementList} />
            </Col>
          </>
        )}
      </Row>
    </Form>
  );
}

export default CashMovementForm;
